import React from 'react'
import PaymentPlanStep1 from './PaymentPlanStep1'
import PaymentPlanStep2 from './PaymentPlanStep2'
import PaymentPlanStep3 from './PaymentPlanStep3'
import PaymentPlanStep4 from './PaymentPlanStep4'
import { usePaymentPlanWizard } from './usePaymentPlanWizard'

/**
 * Payment Plan wizard — render shell. All state, effects, async handlers, and
 * derived data live in `usePaymentPlanWizard`; this component only picks the
 * step component to render and forwards the relevant slice of the wizard's
 * public surface to it.
 *
 * Step layout:
 *   step0 — existing-plan gate (must delete current plan to create a new one)
 *   step1 — plan-type selection + form (collection / settlement / settle-for-zero)
 *   step2 — installments editor
 *   step3 — Send Confirmation
 *   step4 — read-only existing plan (entered when a plan already exists with payments)
 */
export default function PaymentPlanPayment(props) {
  const {
    loanId,
    refreshPaymentSchedule,
    navigateSlider,
    currentDate,
    outstandingTotal,
    customerInfo,
    // True when loan is in `loan_status_resolution` + `loan_sub_status_no_res_plan`
    // (computed in PaymentScheduleSubPanel). Drives Step 1 plan-type-button
    // gating: a resolution loan only sees "Create Resolution Plan", a
    // collections loan only sees the existing three.
    isInResolutionWithoutPlan,
  } = props

  const wizard = usePaymentPlanWizard({
    loanId,
    refreshPaymentSchedule,
    navigateSlider,
    currentDate,
    outstandingTotal,
    customerInfo,
  })

  return (
    <div className="pl-5 py-3">
      {wizard.responseData === null && (
        <div className="is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }}>--- Loading ---</div>
      )}

      {wizard.responseData !== null && (
        <>
          {wizard.step === "step0" && (
            <>
              <p className="has-text-danger" data-testid="step0-existing-plan-message"> A payment plan already exists. To create a new payment plan, you need to delete the current one.</p>
              <table className="table is-narrow table-payment-schedule-panel">
                <tbody>
                  <tr>
                    <td>
                      <button data-testid="step0-delete-payment-plan" className="button is-danger is-small mt-5 p-2" onClick={() => wizard.deletePaymentPlan()}>Delete Payment Plan</button>
                    </td>
                    <td colSpan={2}>
                      <div className="has-text-right">
                        <button
                          type="button"
                          className="button has-background-grey has-text-white is-small mt-5 mr-1"
                          onClick={() => navigateSlider("form_submit")}
                        >
                          <span className="mx-3">Cancel</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {wizard.step === "step1" && (
            <PaymentPlanStep1
              responseData={wizard.responseData}
              outstandingTotal={outstandingTotal}
              planSelection={wizard.planSelection}
              strategy={wizard.strategy}
              isInResolutionWithoutPlan={isInResolutionWithoutPlan}
              getCurrentPaymentInfo={wizard.getCurrentPaymentInfo}
              checkChangesMade={wizard.checkChangesMade}
              control={wizard.control}
              handleSubmit={wizard.handleSubmit}
              onSubmit={wizard.onSubmit}
              onSettleForZeroSubmit={wizard.onSettleForZeroSubmit}
              setValue={wizard.setValue}
              payFrequencyOptions={wizard.payFrequencyOptions}
              payFrequencyValue={wizard.payFrequencyValue}
              setPayFrequencyValue={wizard.setPayFrequencyValue}
              payFrequencyValueOption={wizard.payFrequencyValueOption}
              setPayFrequencyValueOption={wizard.setPayFrequencyValueOption}
              dayOfWeekOptions={wizard.dayOfWeekOptions}
              dayOfMonthOptions={wizard.dayOfMonthOptions}
              weekOfMonthOptions={wizard.weekOfMonthOptions}
              discountData={wizard.discountData}
              currentDate={currentDate}
              today={wizard.today}
              collectionPromo={wizard.collectionPromo}
              settlementDiscountType={wizard.settlementDiscountType}
              handleDiscountTypeChange={wizard.handleDiscountTypeChange}
              watchCustomerPromotionCodeId={wizard.watchCustomerPromotionCodeId}
              discountInfoText={wizard.discountInfoText}
              setDiscountValue={wizard.setDiscountValue}
              setSettlementDiscountType={wizard.setSettlementDiscountType}
              settlementInstallationType={wizard.settlementInstallationType}
              setSettlementInstallationType={wizard.setSettlementInstallationType}
              installmentOptions={wizard.installmentOptions}
              resolutionMaxPayments={wizard.resolutionMaxPayments}
              minPayment={wizard.minPayment}
              saving={wizard.saving}
              isDiscountAboveCap={wizard.isDiscountAboveCap}
              isBalanceBelowMinimum={wizard.isBalanceBelowMinimum}
              balanceBelowMinimumText={wizard.balanceBelowMinimumText}
            />
          )}

          {wizard.step === "step2" && (
            <PaymentPlanStep2
              control={wizard.control}
              collectionResponseData={wizard.collectionResponseData}
              planSelection={wizard.planSelection}
              strategy={wizard.strategy}
              installmentOptions={wizard.installmentOptions}
              refreshStepTwo={wizard.refreshStepTwo}
              stepTwoNumberOfInstallments={wizard.stepTwoNumberOfInstallments}
              setStepTwoNumberOfInstallments={wizard.setStepTwoNumberOfInstallments}
              paymentPlanPaymentsStep2={wizard.paymentPlanPaymentsStep2}
              discountData={wizard.discountData}
              saving={wizard.saving}
              totalAmount={wizard.totalAmount}
              setStep={wizard.setStep}
              checkChangesMade={wizard.checkChangesMade}
              handleSubmit={wizard.handleSubmit}
              onSubmit={wizard.onSubmit}
              watchCustomerPromotionCodeId={wizard.watchCustomerPromotionCodeId}
            />
          )}

          {wizard.step === "step3" && (
            <PaymentPlanStep3
              settlementResponseData={wizard.settlementResponseData}
              planSelection={wizard.planSelection}
              strategy={wizard.strategy}
              paymentPlanPaymentsStep3={wizard.paymentPlanPaymentsStep3}
              formBody={wizard.formBody}
              dropdownOptions={wizard.dropdownOptions}
              saving={wizard.saving}
              setStep={wizard.setStep}
              checkChangesMade={wizard.checkChangesMade}
              handleSubmit={wizard.handleSubmit}
              onSubmit={wizard.onSubmit}
              watchCustomerPromotionCodeId={wizard.watchCustomerPromotionCodeId}
              settlementDiscountType={wizard.settlementDiscountType}
              discountPercentStep1={wizard.discountPercentStep1}
              discountAmountStep1={wizard.discountAmountStep1}
              discountData={wizard.discountData}
            />
          )}

          {wizard.step === "step4" && (
            <PaymentPlanStep4
              responseData={wizard.responseData}
              dropdownOptions={wizard.dropdownOptions}
              saving={wizard.saving}
              displayActionInStep4={wizard.displayActionInStep4}
              setStep={wizard.setStep}
              checkChangesMade={wizard.checkChangesMade}
              handleSubmit={wizard.handleSubmit}
              onSubmit={wizard.onSubmit}
            />
          )}
        </>
      )}
    </div>
  )
}
